import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from 'reactstrap';
import { useUser } from '@clerk/clerk-react';
import { BiAlarmExclamation, BiPurchaseTag, BiReceipt, BiCheckCircle, BiCamera, BiChevronRight } from 'react-icons/bi';
import { fetchWorkerDashboardSummary } from '../../api/dashboardApi';

// Worker-friendly dashboard mirroring the mobile WorkerDashboard. Workers
// land on /dashboard by default after sign-in (the App.jsx index route
// redirects there) so without this page they'd hit a 403 from the
// manager-only GetSummary endpoint and see a broken layout.
//
// Surfaces the same three things the mobile version does:
//   - Requires Attention: failed scans + uncategorised expenses
//   - Recent Receipts: their last 10 uploaded captures
//   - (Mobile also has a local-queue scan banner — irrelevant on web,
//     where there's no offline scan capture flow.)

function timeAgo(iso) {
    const then = new Date(iso).getTime();
    const diff = Date.now() - then;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function jobLabel(item) {
    if (item.jobNumber) {
        return item.jobTitle ? `#${item.jobNumber} · ${item.jobTitle}` : `#${item.jobNumber}`;
    }
    return item.jobTitle ?? 'No job linked';
}

function AttentionRow({ item }) {
    const isFailedScan = item.type === 'FailedScan';
    const Icon = isFailedScan ? BiAlarmExclamation : BiPurchaseTag;
    const accent = isFailedScan ? '#ef4444' : '#d97706';
    const target = item.jobId ? `/jobs/${item.jobId}` : '/jobs';

    return (
        <Link to={target}
              className="d-flex align-items-center gap-3 p-3 mb-2 surface text-decoration-none"
              style={{ color: 'inherit' }}>
            <div className="d-flex align-items-center justify-content-center flex-shrink-0"
                 style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: `${accent}1a` }}>
                <Icon size={20} color={accent} />
            </div>
            <div className="flex-grow-1 min-w-0">
                <div className="fw-semibold text-truncate">{item.description}</div>
                <div className="small text-muted text-truncate">
                    {jobLabel(item)} · {timeAgo(item.createdAt)}
                </div>
            </div>
            <BiChevronRight size={20} className="text-muted flex-shrink-0" />
        </Link>
    );
}

function ReceiptRow({ item }) {
    const target = item.jobId ? `/jobs/${item.jobId}` : '/jobs';
    const filename = item.originalFileName || 'Receipt';

    return (
        <Link to={target}
              className="d-flex align-items-center gap-3 p-3 mb-2 surface text-decoration-none"
              style={{ color: 'inherit' }}>
            <div className="d-flex align-items-center justify-content-center flex-shrink-0"
                 style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: '#26ada91a' }}>
                <BiReceipt size={20} color="#26ada9" />
            </div>
            <div className="flex-grow-1 min-w-0">
                <div className="fw-semibold text-truncate">{filename}</div>
                <div className="small text-muted text-truncate">
                    {jobLabel(item)} · {timeAgo(item.uploadedAt)}
                </div>
            </div>
            <BiChevronRight size={20} className="text-muted flex-shrink-0" />
        </Link>
    );
}

export default function WorkerDashboard() {
    const { user } = useUser();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const result = await fetchWorkerDashboardSummary();
            setData(result);
        } catch { /* silently fail — Sentry will capture */ }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading) {
        return (
            <div className="d-flex justify-content-center py-5">
                <Spinner />
            </div>
        );
    }

    const attention       = data?.attention       ?? [];
    const recentReceipts  = data?.recentReceipts  ?? [];
    const assignedCount   = data?.assignedJobsCount ?? 0;
    const firstName       = user?.firstName;

    return (
        <div>
            <h1 className="fw-bold mb-1">{firstName ? `Hello, ${firstName}` : 'Hello'}</h1>
            <p className="text-muted mb-4">
                {assignedCount === 0
                    ? "You're not assigned to any jobs yet."
                    : `${assignedCount} job${assignedCount === 1 ? '' : 's'} assigned to you.`}
            </p>

            {/* Requires Attention */}
            <div className="d-flex align-items-center gap-2 mb-3">
                <h2 className="fw-semibold fs-4 mb-0">Requires Attention</h2>
                {attention.length > 0 && (
                    <span className="badge bg-danger">{attention.length}</span>
                )}
            </div>
            <div className="mb-4">
                {attention.length === 0 ? (
                    <div className="surface d-flex align-items-center gap-3 p-3">
                        <BiCheckCircle size={28} color="#10b981" className="flex-shrink-0" />
                        <span className="text-muted">
                            All caught up. Nothing needs your attention right now.
                        </span>
                    </div>
                ) : (
                    attention.map(item => (
                        <AttentionRow key={`${item.type}-${item.id}`} item={item} />
                    ))
                )}
            </div>

            {/* Recent Receipts */}
            <h2 className="fw-semibold fs-4 mb-3">Recent Receipts</h2>
            <div className="mb-4">
                {recentReceipts.length === 0 ? (
                    <div className="surface d-flex align-items-center gap-3 p-3">
                        <BiCamera size={28} className="text-muted flex-shrink-0" />
                        <span className="text-muted">
                            You haven't uploaded any receipts yet. Capture one from the mobile app.
                        </span>
                    </div>
                ) : (
                    recentReceipts.map(item => (
                        <ReceiptRow key={item.id} item={item} />
                    ))
                )}
            </div>
        </div>
    );
}
